import type { ReactNode } from "react"
import { useMemo, useRef, useState } from "react"

import { useHasPermission } from "../../../access/use-access"
import type { CatalogView, EmployeeView, ServiceView } from "../../../api/directory.api"
import { fetchCatalog, fetchEmployees, upsertService } from "../../../api/directory.api"
import { isApiProblem } from "../../../api/problem"
import { cx } from "../../../lib/cx"
import { ProblemScreen } from "../../../shell/states"
import { Badge, Button, Chip, Chips, SelectField, Skeleton, Switch } from "../../../ui"
import styles from "../directories.module.css"
import { formatMoney } from "../format-money"
import { CenterState, SearchField, StandHeader } from "../stand"
import { useResource } from "../use-resource"
import type { CatalogFilter } from "./catalog-model"
import { CATALOG_EMPTY, PRICE_HINT, RESOURCE_HINT, buildCatalog } from "./catalog-model"
import { type ServiceForm, emptyForm, formOf, formProblem, minutesOf, requestOf, totalMinutes } from "./service-form"

/**
 * Каталог услуг (3-Б1) и карточка услуги (3-Б2).
 *
 * КАРТОЧКА ОТКРЫВАЕТСЯ ПО СВОЕМУ АДРЕСУ /catalog/:serviceId, хотя стоит рядом со списком:
 * выбранная услуга — это состояние экрана, и ссылку на неё пересылают («посмотри, тут
 * цена не та»). Панель без адреса такую ссылку сделать не даёт.
 *
 * СОХРАНЕНИЕ ПЕРЕПИСЫВАЕТ УСЛУГУ ЦЕЛИКОМ, включая требования к ресурсам и шаги, — поэтому
 * форма собирается из всего, что отдал каталог (service-form.ts). Пока каталог не отдавал
 * «Кресло — 1», кнопка стояла выключенной: сохранение молча стёрло бы требование (долг П0).
 *
 * /catalog/new — новая услуга; шаги составной правятся на своём экране (3-Б3).
 */

const DENIED = "Править каталог может управляющий или владелец"

export function CatalogScreen(props: {
  readonly serviceId: string | null
  readonly onSelectService: (serviceId: string | null) => void
  readonly onOpenSteps: (serviceId: string) => void
  readonly onOpenPrices: () => void
}): ReactNode {
  const canManage = useHasPermission("catalog.services.manage")

  const catalog = useResource((signal) => fetchCatalog(signal), "catalog")
  const employees = useResource((signal) => fetchEmployees(false, signal), "catalog-employees")

  const [filter, setFilter] = useState<CatalogFilter>("active")
  const [search, setSearch] = useState("")

  const model = useMemo(
    () =>
      catalog.data === null
        ? null
        : buildCatalog(catalog.data, employees.data ?? [], { filter, search }),
    [catalog.data, employees.data, filter, search],
  )

  // Без явного выбора открывается первая ВИДИМАЯ услуга списка, а не первая в выгрузке:
  // иначе справа стояла карточка архивной услуги, которой под фильтром «Активные» слева нет.
  const creating = props.serviceId === "new"
  const selected = useMemo(() => {
    if (catalog.data === null || props.serviceId === "new") return null
    if (props.serviceId === null) return model?.categories[0]?.services[0]?.service ?? null
    return catalog.data.services.find((service) => service.id === props.serviceId) ?? null
  }, [catalog.data, model, props.serviceId])

  const cardRef = useRef<HTMLDivElement>(null)

  if (catalog.problem !== null && catalog.data === null) {
    return <ProblemScreen problem={catalog.problem} onRetry={catalog.reload} />
  }

  const empty = catalog.data !== null && catalog.data.services.length === 0

  return (
    <div className={styles.screen}>
      <StandHeader title="Каталог услуг" count={model?.countLabel ?? ""} repeatsSection>
        <Button size="sm" onClick={props.onOpenPrices}>
          Цены по времени
        </Button>
        <Button
          size="sm"
          deniedReason={canManage ? undefined : "Править каталог может управляющий или владелец"}
        >
          + Категория
        </Button>
        <Button
          size="sm"
          variant="primary"
          deniedReason={canManage ? undefined : "Править каталог может управляющий или владелец"}
          onClick={() => {
            props.onSelectService("new")
            revealCard(cardRef.current)
          }}
        >
          + Услуга
        </Button>
      </StandHeader>

      {catalog.loading ? (
        <div className={styles.cardList}>
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} width="100%" height={92} radius={10} />
          ))}
        </div>
      ) : empty ? (
        <CenterState
          title={CATALOG_EMPTY.title}
          text={CATALOG_EMPTY.text}
          note={CATALOG_EMPTY.note}
          stacked
          actions={
            <>
              <Button
                variant="primary"
                deniedReason={canManage ? undefined : "Заводить услуги может управляющий или владелец"}
              >
                {CATALOG_EMPTY.actions[0]}
              </Button>
              <Button deniedReason={canManage ? undefined : "Переносить прайс может управляющий или владелец"}>
                {CATALOG_EMPTY.actions[1]}
              </Button>
              <Button
                variant="ghost"
                deniedReason={canManage ? undefined : "Заводить услуги может управляющий или владелец"}
              >
                {CATALOG_EMPTY.actions[2]}
              </Button>
            </>
          }
        />
      ) : (
        <div className={styles.split}>
          <div className={styles.splitLeft}>
            <div className={styles.toolbar}>
              <SearchField
                label="Найти услугу"
                placeholder="Найти услугу"
                value={search}
                onChange={setSearch}
              />
              <Chips label="Фильтр каталога">
                <Chip
                  active={filter === "active"}
                  onClick={() => {
                    setFilter("active")
                  }}
                >
                  Активные
                </Chip>
                <Chip
                  active={filter === "all"}
                  onClick={() => {
                    setFilter("all")
                  }}
                >
                  Все
                </Chip>
                <Chip
                  active={filter === "orphan"}
                  count={model?.withoutPerformers}
                  countLabel="услуг без исполнителей"
                  onClick={() => {
                    setFilter("orphan")
                  }}
                >
                  Без исполнителей
                </Chip>
              </Chips>
            </div>

            <div className={styles.cardList}>
              {(model?.categories ?? []).length === 0 ? (
                <CenterState
                  title="По этому фильтру услуг нет"
                  text="Снимите фильтр или поищите по другому слову — прайс никуда не делся."
                />
              ) : (
                (model?.categories ?? []).map((category) => (
                  <section key={category.id} className={styles.groupCard}>
                    <div className={styles.groupHead}>
                      <h3 className={styles.groupName}>{category.name}</h3>
                      <span className={styles.groupCount}>{category.countLabel}</span>
                    </div>
                    {category.services.map((row) => (
                      <button
                        key={row.service.id}
                        type="button"
                        className={cx(
                          styles.rowItem,
                          styles.serviceRow,
                          row.service.id === selected?.id && styles.rowItemActive,
                        )}
                        onClick={() => {
                          props.onSelectService(row.service.id)
                          revealCard(cardRef.current)
                        }}
                      >
                        <span className={styles.rowGrow}>
                          <span className={cx(styles.rowTitle, styles.serviceTitle)}>
                            {row.service.name}
                            {row.hasPause ? (
                              <>
                                {" "}
                                <Badge tone="info">составная · пауза</Badge>
                              </>
                            ) : null}
                          </span>
                          <span className={cx(styles.rowSub, styles.serviceSub)}>
                            {row.durationLabel}
                          </span>
                        </span>
                        <span
                          className={cx(
                            row.performersCount === 0 ? styles.rowMetaAlert : styles.rowMeta,
                            styles.serviceMeta,
                          )}
                        >
                          {row.performersLabel}
                        </span>
                        <span className={cx(styles.rowPrice, styles.servicePrice)}>
                          {formatMoney(row.service.priceMinor)}
                        </span>
                        <span className={styles.serviceOnline}>
                          <Badge tone={row.service.isOnlineBookable ? "ok" : "gray"}>
                            {row.onlineLabel}
                          </Badge>
                        </span>
                      </button>
                    ))}
                  </section>
                ))
              )}
            </div>
          </div>

          <div ref={cardRef} className={styles.splitRight}>
            {selected === null && !creating ? (
              <p className={styles.rowSub}>Выберите услугу слева — её карточка откроется здесь.</p>
            ) : catalog.data === null ? null : (
              <ServiceCard
                key={selected === null ? "new" : `${selected.id}:${selected.updatedAt ?? ""}`}
                service={selected}
                catalog={catalog.data}
                employees={employees.data ?? []}
                canManage={canManage}
                onSaved={(id) => {
                  catalog.reload()
                  props.onSelectService(id)
                }}
                onOpenSteps={props.onOpenSteps}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * На узком экране карточка стоит ПОД списком, и нажатие на услугу ничего не меняло
 * на виду: карточка обновлялась ниже края экрана, и казалось, что нажатие не сработало.
 * Докручиваем к ней — только если её верх не виден. На широком экране карточка стоит
 * рядом со списком, её верх всегда на виду, и страница не дёргается.
 */
function revealCard(card: HTMLElement | null): void {
  if (card === null || typeof window === "undefined") return
  if (card.getBoundingClientRect().top < window.innerHeight * 0.6) return
  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  card.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" })
}

function ServiceCard(props: {
  readonly service: ServiceView | null
  readonly catalog: CatalogView
  readonly employees: readonly EmployeeView[]
  readonly canManage: boolean
  readonly onSaved: (serviceId: string) => void
  readonly onOpenSteps: (serviceId: string) => void
}): ReactNode {
  const { service } = props
  const [form, setForm] = useState<ServiceForm>(() =>
    service === null ? emptyForm(props.catalog.categories[0]?.id ?? null) : formOf(service),
  )
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const patch = (next: Partial<ServiceForm>): void => {
    setForm({ ...form, ...next })
    setProblem(null)
    setSaved(false)
  }

  const performers =
    service === null
      ? []
      : props.employees.filter((employee) => employee.archivedAt === null && employee.serviceIds.includes(service.id))
  const composite = form.kind === "composite"
  const minutes = composite ? totalMinutes(form.steps) : (minutesOf(form.minutes) ?? 0)
  const before = minutesOf(form.bufferBefore) ?? 0
  const after = minutesOf(form.bufferAfter) ?? 0
  const denied = props.canManage ? undefined : DENIED
  const usedTypes = new Set(form.resources.map((item) => item.resourceTypeId))
  const freeTypes = props.catalog.resourceTypes.filter((type) => type.isActive && !usedTypes.has(type.id))

  const save = async (copy: boolean): Promise<void> => {
    const local = formProblem(form)
    if (local !== null) {
      setProblem(local)
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      const request = copy
        ? requestOf({ ...form, name: `${form.name.trim()} (копия)` }, null)
        : requestOf(form, service)
      const result = await upsertService(
        copy && service !== null ? { ...request, branchId: service.branchId, ord: service.ord, capacity: service.capacity } : request,
      )
      setSaved(true)
      props.onSaved(result.id)
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setProblem(
        caught.status === 409
          ? "Услугу изменили, пока вы её правили. Обновите страницу — свои правки придётся внести ещё раз."
          : (caught.detail ?? caught.title),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className={styles.sectionLabel}>{service === null ? "Новая услуга" : "Карточка услуги"}</div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Название</span>
        <input
          className={styles.input}
          value={form.name}
          maxLength={200}
          onChange={(event) => patch({ name: event.target.value })}
        />
      </label>

      <SelectField
        label="Категория"
        value={form.categoryId ?? ""}
        options={[
          { value: "", label: "Без категории" },
          ...props.catalog.categories.map((category) => ({ value: category.id, label: category.name })),
        ]}
        onChange={(event) => patch({ categoryId: event.target.value === "" ? null : event.target.value })}
      />

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Длительность, мин</span>
        {composite ? (
          <>
            <input className={styles.input} value={`${minutes} мин — из шагов`} readOnly />
            <small className={styles.rowSub}>
              Составная услуга: длительность — сумма шагов, вручную не вводится.
            </small>
          </>
        ) : (
          <input
            className={cx(styles.input, styles.tabularNums)}
            inputMode="numeric"
            value={form.minutes}
            onChange={(event) => patch({ minutes: event.target.value.replace(/[^\d]/g, "") })}
          />
        )}
      </label>
      {service === null ? null : (
        <Button
          size="sm"
          deniedReason={denied}
          onClick={() => props.onOpenSteps(service.id)}
        >
          {composite ? "Шаги и паузы →" : "Сделать составной — с паузой →"}
        </Button>
      )}

      <div className={styles.previewTiles}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Буфер до, мин</span>
          <input
            className={styles.input}
            inputMode="numeric"
            value={form.bufferBefore}
            onChange={(event) => patch({ bufferBefore: event.target.value.replace(/[^\d]/g, "") })}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Буфер после, мин</span>
          <input
            className={styles.input}
            inputMode="numeric"
            value={form.bufferAfter}
            onChange={(event) => patch({ bufferAfter: event.target.value.replace(/[^\d]/g, "") })}
          />
        </label>
      </div>

      <div className={styles.hintBox}>
        Клиент увидит «{minutes} мин». В расписании мастера услуга займёт {minutes + before + after} мин: {before} до
        и {after} после.
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Цена, ₽</span>
        <input
          className={cx(styles.input, styles.tabularNums)}
          inputMode="decimal"
          value={form.price}
          onChange={(event) => patch({ price: event.target.value })}
        />
        <small className={styles.rowSub}>{PRICE_HINT}</small>
      </label>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Требует ресурс</span>
        {form.resources.length === 0 ? (
          <p className={styles.rowSub}>Не требует — планировщик смотрит только на мастера.</p>
        ) : (
          form.resources.map((item, index) => {
            const type = props.catalog.resourceTypes.find((candidate) => candidate.id === item.resourceTypeId)
            return (
              <div key={item.resourceTypeId} className={styles.sideRow}>
                <span>{type?.name ?? "Ресурс"}</span>
                <input
                  className={cx(styles.input, styles.tabularNums)}
                  aria-label={`Сколько нужно: ${type?.name ?? "ресурс"}`}
                  inputMode="numeric"
                  value={String(item.qty)}
                  onChange={(event) => {
                    const qty = Math.min(10, Math.max(1, Number(event.target.value.replace(/[^\d]/g, "")) || 1))
                    patch({ resources: form.resources.map((r, i) => (i === index ? { ...r, qty } : r)) })
                  }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  deniedReason={denied}
                  onClick={() => patch({ resources: form.resources.filter((_, i) => i !== index) })}
                >
                  Убрать
                </Button>
              </div>
            )
          })
        )}
        {freeTypes.length === 0 ? null : (
          <SelectField
            label="Добавить ресурс"
            value=""
            options={[
              { value: "", label: "Выберите тип ресурса" },
              ...freeTypes.map((type) => ({ value: type.id, label: type.name })),
            ]}
            onChange={(event) => {
              if (event.target.value === "") return
              patch({ resources: [...form.resources, { resourceTypeId: event.target.value, qty: 1 }] })
            }}
          />
        )}
        <small className={styles.rowSub}>{RESOURCE_HINT}</small>
      </div>

      <Switch checked={form.isOnlineBookable} onChange={(checked) => patch({ isOnlineBookable: checked })}>
        Онлайн-запись
      </Switch>
      <Switch checked={form.isActive} onChange={(checked) => patch({ isActive: checked })}>
        Активна
      </Switch>

      {service === null ? null : (
        <div>
          <div className={styles.fieldLabel}>Исполнители · {performers.length}</div>
          <div className={styles.tagRow}>
            {performers.map((employee) => (
              <Badge key={employee.id} tone="gray">
                {employee.displayName}
              </Badge>
            ))}
            {performers.length === 0 ? <Badge tone="warn">никто — записать нельзя</Badge> : null}
          </div>
        </div>
      )}

      {problem === null ? null : <div className={styles.dangerBox}>{problem}</div>}
      {saved ? <p className={styles.rowSub}>Сохранено. Новая цена и длительность — для будущих записей.</p> : null}

      <div className={styles.centerActions}>
        <Button variant="primary" loading={busy} deniedReason={denied} onClick={() => void save(false)}>
          {service === null ? "Создать услугу" : "Сохранить"}
        </Button>
        {service === null ? null : (
          <Button deniedReason={denied} onClick={() => void save(true)}>
            Копировать услугу
          </Button>
        )}
      </div>
    </>
  )
}
